import React, { useCallback, useEffect, useRef, useState } from 'react'
import styled from 'styled-components';
import { ActionWrapper } from '../searchQuery/QueryCreation';
import SearchIcon from '../../assets/icons/SearchIcon';
import { StyledInput } from '../../components/listing/ExpandableSearch';
import { contactRolesApi } from '../../api/contactRoles';
import { debounce } from 'lodash';
import RoleListBar from './RoleListBar';
import { SkeletonCell } from '../../components/SkeletonCell';
import { useTheme } from '../../middleware/ThemeContext';
import { WhiteContainer } from './EditRolePermissions';
import StyledText from '../../components/StyledComponents/StyledText';
import { useOutletContext } from 'react-router-dom';
import NoData from '../../components/listing/NoData';

interface RolesSidebarList {
    selectedID: number | undefined;
    onClick: (id: number) => void;
    setRoleName: (name: string) => void;
    // when true, prevent any automatic selection of the first role
    lockSelection?: boolean;
}
type OutletContextType = {
    setSelectedEntity: (type: "roles", id: number) => void;
    setRefreshTrigger: React.Dispatch<React.SetStateAction<number>>;
    refreshTrigger: number;
    onEditRole: (id: number) => void;
};

const CustomRolesSidebarList: React.FC<RolesSidebarList> = ({ selectedID, onClick, setRoleName, lockSelection = false }) => {
    const { theme } = useTheme();
    const [searchValue, setSearchValue] = useState<string>('');
    const [loading, setLoading] = useState(true);
    const [roles, setRoles] = useState<any[]>([]);
    const [hasMore, setHasMore] = useState(true);
    const [page, setPage] = useState(1);
    const perPage = 10;
    const isResetting = useRef(false);
    
    const {
        refreshTrigger,
        onEditRole,
    } = useOutletContext<OutletContextType>();

    const observer = useRef<IntersectionObserver | null>(null);
    const lastItemRef = useCallback((node: HTMLDivElement | null) => {
    if (loading) return;
    if (observer.current) observer.current.disconnect();

    observer.current = new IntersectionObserver(entries => {
        if (entries[0].isIntersecting && hasMore) {
        setPage(prev => prev + 1);
        }
    });

    if (node) observer.current.observe(node);
    }, [loading, hasMore]);

    const fetchRolesData = async (pageNumber: number, query: string = '', reset = false) => {
        setLoading(true);
        if(reset){
            setRoles([]);
        }
        const sortDir = 'asc';
        try {
            const params = {
            page: pageNumber,
            perPage,
            query,
            sort: { field: 'name', direction: sortDir === 'asc' ? 'ASC' : 'DESC' as 'ASC' | 'DESC' },
            filters: {},
            visibleColumns: [],
            };
        // const [allRoles] = await Promise.all([
        //     contactRolesApi.getContactRoles(pageNumber, perPage, query)
        // ]);
        const res = await contactRolesApi.getContactRoles(
            params.page,
            params.perPage,
            undefined,
            params.query,
            // params.filters,
            // params.visibleColumns || undefined,
        );

        const newRoles = res?.roles || [];

        // Only auto-select the first role when there is no selectedID
        // and the parent did not request to lock selection (e.g., param-provided id).
        if(!selectedID && !lockSelection && newRoles.length > 0 && pageNumber === 1 && !query){
            onClick(newRoles[0].id);
            setRoleName(newRoles[0].name);
        }

        setRoles(prev => reset ? newRoles : [...prev, ...newRoles]);
        setHasMore(newRoles.length === perPage);
        setLoading(false);
        } catch (err) {
        console.error('Failed to load roles', err);
        setLoading(false);
        }
    };

    useEffect(() => {
        if (isResetting.current) {
            isResetting.current = false;
            return;
        }
        fetchRolesData(page, searchValue, page === 1);
    }, [page]);

    const debouncedSearch = useRef(
    debounce((query: string) => {
        setPage(1); // Reset to first page
        fetchRolesData(1, query, true); // true = reset
    }, 500)
    ).current;

    const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
        setSearchValue(e.target.value);
        debouncedSearch(e.target.value);
    };

    const handleEdit = (id: number) => {
        onEditRole(id);
    }

    useEffect(() => {
        if (refreshTrigger) {
            isResetting.current = true;
            setPage(1);
            setSearchValue('');
            // Optional: force immediate fetch for page 1
            fetchRolesData(1, '', true);
        }
    }, [refreshTrigger]);

  return (
    <WhiteContainer theme={theme} style={{ padding: '12px 10px' }}>
        <div>
            <ActionWrapper theme={theme}>
                <SearchIcon color={theme.CorporateTheme.colors.textSecondary} />
                <StyledInput
                type="text"
                placeholder="Search or type a command"
                value={searchValue}
                onChange={handleSearch}
                theme={theme}
                />
            </ActionWrapper>
        </div>
        <div className='mt-4' style={{ height: 'calc(100vh - 365px)', overflowY: 'auto' }}>
            {loading && roles.length === 0 ? 
                <div>
                    {/* <SkeletonCell style={{ height: "41px" }} className='mb-4' /> */}
                    {[...Array(8)].map((_, i) => (
                            <div key={i}>
                                <SkeletonCell style={{ height: "43px", marginBottom: "8px" }} className='me-3 mb-2' />
                            </div>
                    ))}
                </div>
            : roles.length === 0 ? (
                <NoData 
                    title='No roles found'
                    containerHeight={450}
                />
            ) :
                <>
                    {
                        roles.map((role, index) => (
                            <div className='me-3' key={index} ref={index === roles.length - 1 ? lastItemRef : null}>
                            <RoleListBar id={role.id} activeRole={selectedID} title={role.name} assignedUsersCount={role.contactRoles.length} isStandard={role.isStandard} onEdit={handleEdit} onClick={() => {onClick(role.id); setRoleName(role.name) }} />
                            </div>
                        ))
                    }
                    {loading && roles.length > 0 && 
                        <div className='text-center fst-italic'>
                            <StyledText textColor={theme.CorporateTheme.colors.textSecondary}> Loading more roles...</StyledText>
                        </div>
                    }
                </>
            }
        </div>
    </WhiteContainer>
  )
}

export default CustomRolesSidebarList